import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Car, User, Phone, Calendar, MapPin, Check, X, Eye, Trash2, Clock, AlertTriangle, CheckCircle, XCircle, Edit, Image } from 'lucide-react';
import api from '../../../../config/api';

const AdminVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  
  // Fetch all vehicles
  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/vehicles');
      setVehicles(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchVehicles();
  }, []);
  
  // Handle view details
  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailsModal(true);
  };
  
  // Handle edit vehicle
  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditFormData({
      ownerName: vehicle.ownerName,
      contactNumber: vehicle.contactNumber,
      vehicleType: vehicle.vehicleType,
      licensePlate: vehicle.licensePlate,
      capacity: vehicle.capacity,
      state: vehicle.state,
      city: vehicle.city,
      pincode: vehicle.pincode,
      availabilityDate: vehicle.availabilityDate ? new Date(vehicle.availabilityDate).toISOString().split('T')[0] : '',
      availableDays: vehicle.availableDays,
      isCurrentlyAvailable: vehicle.isCurrentlyAvailable,
      additionalNotes: vehicle.additionalNotes || ''
    });
    setShowEditModal(true);
  };
  
  // Handle save edit
  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const response = await api.put(`/admin/vehicles/${selectedVehicle._id}`, editFormData);
      
      // Update local state
      setVehicles(vehicles.map(vehicle => 
        vehicle._id === selectedVehicle._id ? { ...vehicle, ...editFormData } : vehicle
      ));
      
      toast.success('Vehicle details updated successfully');
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating vehicle details:', err);
      toast.error('Failed to update vehicle details');
    } finally {
      setEditLoading(false);
    }
  };
  
  // Handle form change
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle status update
  const handleStatusUpdate = async (id, status) => {
    setStatusUpdateLoading(true);
    try {
      await api.put(`/admin/vehicles/${id}/status`, {
        status,
        message: statusMessage
      });
      
      // Update local state
      setVehicles(vehicles.map(vehicle => 
        vehicle._id === id ? { ...vehicle, status } : vehicle
      ));
      
      toast.success(`Vehicle ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Error updating vehicle status:', err);
      toast.error('Failed to update vehicle status');
    } finally {
      setStatusUpdateLoading(false);
      setStatusMessage('');
    }
  };
  
  // Handle delete vehicle
  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      await api.delete(`/admin/vehicles/${id}`);
      setVehicles(vehicles.filter(vehicle => vehicle._id !== id));
      toast.success('Vehicle deleted successfully');
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      toast.error('Failed to delete vehicle');
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Active</span>;
      case 'inactive':
        return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">Inactive</span>;
      case 'pending':
        return <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Pending</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Rejected</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">{status}</span>;
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Vehicle Management</h1>
        <button 
          onClick={fetchVehicles}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertTriangle className="mr-2" size={18} />
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : vehicles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <Car className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-xl font-medium text-gray-700 mb-2">No Vehicles Found</h3>
          <p className="text-gray-500">There are no registered vehicles in the system yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered On</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map((vehicle) => (
                  <tr key={vehicle._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{vehicle.ownerName}</div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Phone size={14} className="mr-1" /> {vehicle.contactNumber}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{vehicle.vehicleType}</div>
                      <div className="text-sm text-gray-500">{vehicle.licensePlate}</div>
                      <div className="text-sm text-gray-500">Capacity: {vehicle.capacity}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.city}, {vehicle.state}</div>
                      <div className="text-sm text-gray-500">{vehicle.pincode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(vehicle.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(vehicle.registrationDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(vehicle)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEditVehicle(vehicle)}
                        className="text-orange-600 hover:text-orange-900 mr-3"
                        title="Edit Vehicle"
                      >
                        <Edit size={18} />
                      </button>
                      {vehicle.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(vehicle._id, 'active')}
                            className="text-green-600 hover:text-green-900 mr-3"
                            title="Approve"
                          >
                            <Check size={18} />
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(vehicle._id, 'rejected')}
                            className="text-red-600 hover:text-red-900"
                            title="Reject"
                          >
                            <X size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Vehicle Details Modal */}
      {showDetailsModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <Car className="mr-2 text-blue-600" /> Vehicle Details
                </h2>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Owner Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <User className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Owner Name</p>
                        <p className="font-medium">{selectedVehicle.ownerName}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Contact Number</p>
                        <p className="font-medium">{selectedVehicle.contactNumber}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Vehicle Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <Car className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Vehicle Type</p>
                        <p className="font-medium capitalize">{selectedVehicle.vehicleType}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 text-blue-500 mt-1 w-4 flex justify-center">#</div>
                      <div>
                        <p className="text-sm text-gray-500">License Plate</p>
                        <p className="font-medium">{selectedVehicle.licensePlate}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 text-blue-500 mt-1 w-4 flex justify-center">P</div>
                      <div>
                        <p className="text-sm text-gray-500">Passenger Capacity</p>
                        <p className="font-medium">{selectedVehicle.capacity}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Location</h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <MapPin className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="font-medium">{selectedVehicle.city}, {selectedVehicle.state}</p>
                        <p className="text-sm text-gray-500">Pincode: {selectedVehicle.pincode}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Availability</h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <Calendar className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Available From</p>
                        <p className="font-medium">{formatDate(selectedVehicle.availabilityDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Clock className="mr-2 text-blue-500 mt-1" size={16} />
                      <div>
                        <p className="text-sm text-gray-500">Available For</p>
                        <p className="font-medium">
                          {selectedVehicle.availableDays === 365 ? '24/7 (Always Available)' : `${selectedVehicle.availableDays} days`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 text-blue-500 mt-1 w-4 flex justify-center">
                        {selectedVehicle.isCurrentlyAvailable ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Currently Available</p>
                        <p className="font-medium">{selectedVehicle.isCurrentlyAvailable ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedVehicle.additionalNotes && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Notes</h3>
                  <p className="text-gray-700">{selectedVehicle.additionalNotes}</p>
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Vehicle Image</h3>
                <div className="mt-2">
                  {selectedVehicle.vehicleImage ? (
                    <div>
                      {selectedVehicle.vehicleImage.startsWith('data:image') ? (
                        // Display base64 image directly
                        <img 
                          src={selectedVehicle.vehicleImage} 
                          alt="Vehicle" 
                          className="max-w-full h-auto rounded-lg border border-gray-200"
                          onError={(e) => {
                            if (e.target) {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x300?text=No+Image+Available';
                            }
                          }}
                        />
                      ) : (
                        // Try to load from uploads directory
                        <img 
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/uploads/${selectedVehicle.vehicleImage}`} 
                          alt="Vehicle" 
                          className="max-w-full h-auto rounded-lg border border-gray-200"
                          onError={(e) => {
                            if (e.target) {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x300?text=No+Image+Available';
                            }
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-6 bg-gray-100 rounded-lg border border-gray-200">
                      <Image className="text-gray-400 mb-2" size={48} />
                      <p className="text-gray-500">No vehicle image available</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Status Management</h3>
                <div className="flex items-center mb-4">
                  <div className="mr-2 px-3 py-1 rounded-full text-sm font-medium">
                    Current Status: {getStatusBadge(selectedVehicle.status)}
                  </div>
                </div>
                
                {selectedVehicle.status === 'pending' && (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message (Optional)
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Add a message to be sent to the vehicle owner"
                        value={statusMessage}
                        onChange={(e) => setStatusMessage(e.target.value)}
                      ></textarea>
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleStatusUpdate(selectedVehicle._id, 'active')}
                        disabled={statusUpdateLoading}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <Check className="mr-1" size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedVehicle._id, 'rejected')}
                        disabled={statusUpdateLoading}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        <X className="mr-1" size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleDeleteVehicle(selectedVehicle._id)}
                    className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <Trash2 className="mr-1" size={16} />
                    Delete Vehicle
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Vehicle Modal */}
      {showEditModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <Edit className="mr-2 text-orange-600" /> Edit Vehicle
                </h2>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Owner Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Owner Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                        <input
                          type="text"
                          name="ownerName"
                          value={editFormData.ownerName || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                        <input
                          type="text"
                          name="contactNumber"
                          value={editFormData.contactNumber || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Vehicle Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Vehicle Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                        <select
                          name="vehicleType"
                          value={editFormData.vehicleType || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Vehicle Type</option>
                          <option value="car">Car</option>
                          <option value="bike">Bike</option>
                          <option value="ambulance">Ambulance</option>
                          <option value="van">Van</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
                        <input
                          type="text"
                          name="licensePlate"
                          value={editFormData.licensePlate || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Passenger Capacity</label>
                        <input
                          type="number"
                          name="capacity"
                          value={editFormData.capacity || ''}
                          onChange={handleEditFormChange}
                          min="1"
                          max="6"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Location */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Location</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          name="state"
                          value={editFormData.state || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          name="city"
                          value={editFormData.city || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          value={editFormData.pincode || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Availability */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Availability</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
                        <input
                          type="date"
                          name="availabilityDate"
                          value={editFormData.availabilityDate || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Available For (Days)</label>
                        <select
                          name="availableDays"
                          value={editFormData.availableDays || ''}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Availability</option>
                          <option value="365">24/7 (Always Available)</option>
                          <option value="7">7 Days</option>
                          <option value="14">14 Days</option>
                          <option value="30">30 Days</option>
                          <option value="90">3 Months</option>
                          <option value="180">6 Months</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isCurrentlyAvailable"
                          name="isCurrentlyAvailable"
                          checked={editFormData.isCurrentlyAvailable || false}
                          onChange={handleEditFormChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isCurrentlyAvailable" className="ml-2 block text-sm text-gray-700">
                          Currently Available
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Notes</h3>
                  <textarea
                    name="additionalNotes"
                    value={editFormData.additionalNotes || ''}
                    onChange={handleEditFormChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes about the vehicle"
                  ></textarea>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVehicles;